/* ==========================================================================
   Griffe & Cime — fiche produit (lot C)
   <gc-galerie>       : diapositives à défilement (scroll-snap), flèches, vignettes, compteur
   <gc-produit>       : sélection de variante sans rechargement, URL ?variant=, ajout AJAX
   <gc-barre-produit> : barre collante (ordinateur) et barre d'achat basse (téléphone)
   Comparateur        : sur la fiche, « Choisir » sélectionne la hauteur sans recharger.
   Aucun texte en dur : les libellés viennent du JSON rendu par la section ou d'attributs data-*.
   Événements : émet « gc:variante » (detail : { produit, variante }) et
   « gc:panier:ajout » (detail : { item }) ; le tiroir panier du lot A écoute ce dernier.
   ========================================================================== */
(() => {
  if (window.GCProduitCharge) return;
  window.GCProduitCharge = true;

  const mouvementReduit = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const remplacer = (modele, valeurs) =>
    Object.keys(valeurs).reduce((texte, cle) => texte.split(`[${cle}]`).join(String(valeurs[cle])), modele || '');

  /* --- Galerie ---------------------------------------------------------- */
  class GcGalerie extends HTMLElement {
    connectedCallback() {
      if (this.pret) return;
      this.piste = this.querySelector('[data-gc-piste]');
      if (!this.piste) return;
      this.pret = true;
      this.diapos = Array.from(this.piste.querySelectorAll('[data-media-id]'));
      this.vignettes = Array.from(this.querySelectorAll('[data-gc-vignette]'));
      this.listeVignettes = this.querySelector('[data-gc-vignettes]');
      this.plus = this.querySelector('[data-gc-plus]');
      this.compteur = this.querySelector('[data-gc-galerie-compteur]');
      this.points = Array.from(this.querySelectorAll('[data-gc-point]'));
      this.prec = this.querySelector('[data-gc-prec]');
      this.suiv = this.querySelector('[data-gc-suiv]');
      this.index = 0;

      if (this.prec) this.prec.addEventListener('click', () => this.aller(this.index - 1));
      if (this.suiv) this.suiv.addEventListener('click', () => this.aller(this.index + 1));
      this.vignettes.forEach((lien) => {
        lien.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.aller(Number(lien.dataset.gcVignette));
        });
      });
      if (this.plus) this.plus.addEventListener('click', () => this.basculerVignettes());

      if ('IntersectionObserver' in window) {
        this.observateur = new IntersectionObserver((entrees) => {
          entrees.forEach((entree) => {
            if (entree.isIntersecting) this.marquer(this.diapos.indexOf(entree.target));
          });
        }, { root: this.piste, threshold: 0.6 });
        this.diapos.forEach((diapo) => this.observateur.observe(diapo));
      } else {
        this.piste.addEventListener('scroll', () => {
          const largeur = this.piste.clientWidth || 1;
          this.marquer(Math.round(this.piste.scrollLeft / largeur));
        }, { passive: true });
      }

      const initial = this.dataset.mediaInitial;
      if (initial) this.allerMedia(initial, false);
      this.marquer(this.index);
    }

    aller(index, anime = true) {
      if (!this.diapos.length) return;
      const cible = Math.max(0, Math.min(this.diapos.length - 1, index));
      const diapo = this.diapos[cible];
      this.piste.scrollTo({ left: diapo.offsetLeft, behavior: anime && !mouvementReduit() ? 'smooth' : 'auto' });
      this.marquer(cible);
    }

    allerMedia(idMedia, anime = true) {
      if (!idMedia) return;
      const index = this.diapos.findIndex((diapo) => diapo.dataset.mediaId === String(idMedia));
      if (index > -1) this.aller(index, anime);
    }

    marquer(index) {
      if (index < 0 || index >= this.diapos.length) return;
      this.index = index;
      const total = this.diapos.length;
      if (this.compteur) this.compteur.textContent = remplacer(this.compteur.dataset.modele, { n: index + 1 });
      this.vignettes.forEach((lien, i) => {
        if (i === index) lien.setAttribute('aria-current', 'true');
        else lien.removeAttribute('aria-current');
      });
      if (this.points.length) {
        const actif = total > 1 ? Math.round((index / (total - 1)) * (this.points.length - 1)) : 0;
        this.points.forEach((point, i) => point.classList.toggle('gc-galerie__point--actif', i === actif));
      }
      if (this.prec) this.prec.disabled = index === 0;
      if (this.suiv) this.suiv.disabled = index === total - 1;
      if (this.plus && this.listeVignettes) {
        const replie = this.listeVignettes.dataset.replie === 'true';
        const visibles = this.listeVignettes.querySelectorAll('.gc-galerie__vignette-item:not(.gc-galerie__plus-item)').length;
        const limite = Math.min(visibles, 5);
        this.plus.classList.toggle('gc-galerie__plus--actif', replie && index >= limite);
      }
    }

    basculerVignettes() {
      if (!this.listeVignettes || !this.plus) return;
      const ouvrir = this.listeVignettes.dataset.replie === 'true';
      this.listeVignettes.dataset.replie = ouvrir ? 'false' : 'true';
      this.plus.setAttribute('aria-expanded', String(ouvrir));
      const court = this.plus.querySelector('[data-gc-plus-court]');
      const texte = this.plus.querySelector('[data-gc-plus-texte]');
      if (court) court.textContent = ouvrir ? this.plus.dataset.courtFermer : this.plus.dataset.courtOuvrir;
      if (texte) texte.textContent = ouvrir ? this.plus.dataset.texteFermer : this.plus.dataset.texteOuvrir;
      this.marquer(this.index);
    }
  }

  /* --- Produit : variantes et panier ------------------------------------ */
  class GcProduit extends HTMLElement {
    connectedCallback() {
      if (this.pret) return;
      const script = this.querySelector('[data-gc-produit-donnees]');
      if (!script) return;
      try {
        this.donnees = JSON.parse(script.textContent);
      } catch (erreur) {
        return;
      }
      this.pret = true;
      this.variantes = this.donnees.variantes || [];
      this.textes = this.donnees.textes || {};
      this.formulaire = this.querySelector('form.gc-produit__formulaire');
      this.select = this.querySelector('[data-gc-select]');
      this.bouton = this.querySelector('[data-gc-ajout]');
      this.statut = this.querySelector('[data-gc-statut]');
      this.prix = this.querySelector('[data-gc-prix]');
      this.resume = this.querySelector('[data-gc-resume]');
      this.details = this.querySelector('[data-gc-details]');
      this.galerie = this.querySelector('gc-galerie');
      this.quantite = this.querySelector('[data-gc-quantite]');
      this.groupes = Array.from(this.querySelectorAll('[data-gc-option]'));

      this.groupes.forEach((groupe) => {
        groupe.querySelectorAll('[data-gc-choix]').forEach((input) => {
          input.addEventListener('change', () => this.surChoix());
        });
      });
      this.querySelectorAll('[data-gc-produit-qte]').forEach((bouton) => {
        bouton.addEventListener('click', () => this.ajusterQuantite(Number(bouton.dataset.gcProduitQte)));
      });
      if (this.formulaire) this.formulaire.addEventListener('submit', (evt) => this.ajouter(evt));

      // État restauré par le navigateur (retour arrière) : resynchroniser sans toucher l'URL.
      const choix = this.optionsChoisies();
      const variante = this.trouver(choix);
      if (variante && this.select && String(variante.id) !== this.select.value) this.appliquer(variante, choix, false);
      else this.majChoix(choix);
    }

    optionsChoisies() {
      return this.groupes.map((groupe) => {
        const coche = groupe.querySelector('[data-gc-choix]:checked');
        return coche ? coche.value : null;
      });
    }

    trouver(choix) {
      return this.variantes.find((v) => v.options.every((valeur, i) => choix[i] === null || choix[i] === undefined || valeur === choix[i])
        && v.options.length === choix.length) || null;
    }

    surChoix() {
      const choix = this.optionsChoisies();
      this.appliquer(this.trouver(choix), choix, true);
    }

    choisirVariante(id) {
      const variante = this.variantes.find((v) => String(v.id) === String(id));
      if (!variante) return false;
      this.groupes.forEach((groupe, i) => {
        groupe.querySelectorAll('[data-gc-choix]').forEach((input) => {
          input.checked = input.value === variante.options[i];
        });
      });
      this.appliquer(variante, variante.options.slice(), true);
      return true;
    }

    appliquer(variante, choix, majUrl) {
      this.variante = variante;
      this.majChoix(choix);
      this.majBouton(variante);
      if (variante) {
        if (this.select) this.select.value = String(variante.id);
        if (this.prix && variante.prixHtml) this.prix.innerHTML = variante.prixHtml;
        if (this.resume) {
          this.resume.innerHTML = variante.resumeHtml || '';
          this.resume.hidden = !variante.resumeHtml;
        }
        if (this.details) this.details.textContent = variante.details || '';
        if (this.galerie && this.galerie.allerMedia && variante.media) this.galerie.allerMedia(variante.media);
        if (majUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variante.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      }
      if (this.statut) this.statut.innerHTML = '';
      document.dispatchEvent(new CustomEvent('gc:variante', {
        detail: { produit: this.donnees.produit, variante }
      }));
    }

    /* Légendes, disponibilité de chaque choix, prix et emprise des cartes de hauteur. */
    majChoix(choix) {
      const iH = this.donnees.indexHauteur;
      this.groupes.forEach((groupe, i) => {
        const valeurLegende = groupe.querySelector('[data-gc-valeur]');
        if (valeurLegende && choix[i]) valeurLegende.textContent = choix[i];
        groupe.querySelectorAll('[data-gc-choix]').forEach((input) => {
          const essai = choix.slice();
          essai[i] = input.value;
          let variante = this.trouver(essai);
          const etiquette = input.closest('label');
          if (!etiquette) return;
          let dispo = 'absent';
          if (variante) dispo = variante.disponible ? 'oui' : 'non';
          etiquette.dataset.dispo = dispo;
          const texteDispo = etiquette.querySelector('[data-gc-dispo-texte]');
          if (texteDispo) {
            if (dispo === 'non') texteDispo.textContent = this.textes.epuise || '';
            else if (dispo === 'absent') texteDispo.textContent = this.textes.indisponible || '';
            else texteDispo.textContent = '';
          }
          if (i === iH) {
            if (!variante) variante = this.variantes.find((v) => v.options[i] === input.value) || null;
            const prix = etiquette.querySelector('[data-gc-carte-prix]');
            const emprise = etiquette.querySelector('[data-gc-carte-emprise]');
            if (variante && prix) prix.textContent = variante.prix;
            if (variante && emprise && variante.emprise) emprise.textContent = variante.emprise;
          }
        });
      });
    }

    majBouton(variante) {
      if (!this.bouton) return;
      const long = this.bouton.querySelector('[data-gc-ajout-long]');
      const court = this.bouton.querySelector('[data-gc-ajout-court]');
      let texteLong = this.textes.indisponible;
      let texteCourt = this.textes.indisponible;
      let actif = false;
      if (variante && variante.disponible) {
        texteLong = variante.boutonPrix;
        texteCourt = this.textes.ajouter;
        actif = true;
      } else if (variante) {
        texteLong = this.textes.epuise;
        texteCourt = this.textes.epuise;
      }
      this.bouton.disabled = !actif;
      if (long) long.textContent = texteLong;
      if (court) court.textContent = texteCourt;
    }

    ajusterQuantite(pas) {
      if (!this.quantite) return;
      const actuelle = parseInt(this.quantite.value, 10) || 1;
      this.quantite.value = String(Math.max(1, actuelle + pas));
    }

    message(texte, type) {
      if (!this.statut) return;
      this.statut.innerHTML = '';
      const p = document.createElement('p');
      p.className = `gc-message gc-message--${type}`;
      p.textContent = texte;
      this.statut.appendChild(p);
    }

    async ajouter(evt) {
      const gc = window.GC;
      if (!gc || !gc.routes || !gc.routes.cartAdd || !window.fetch) return; // repli : envoi natif
      evt.preventDefault();
      if (!this.bouton || this.bouton.disabled || this.envoi) return;
      if (this.quantite && (parseInt(this.quantite.value, 10) || 0) < 1) this.quantite.value = '1';

      this.envoi = true;
      this.bouton.setAttribute('aria-busy', 'true');
      this.classList.add('gc-produit--envoi');
      if (this.statut) this.statut.innerHTML = '';
      try {
        const reponse = await fetch(`${gc.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.formulaire)
        });
        const item = await reponse.json().catch(() => ({}));
        if (!reponse.ok) {
          this.message(reponse.status === 422 ? this.textes.erreurQuantite : this.textes.erreur, 'erreur');
          return;
        }
        if (gc.cartType === 'page') {
          window.location.href = gc.routes.cart;
          return;
        }
        document.dispatchEvent(new CustomEvent('gc:panier:ajout', { detail: { item } }));
        const modele = this.querySelector('[data-gc-modele-succes]');
        if (modele && this.statut) {
          this.statut.innerHTML = '';
          this.statut.appendChild(modele.content.cloneNode(true));
        }
      } catch (erreur) {
        this.message(this.textes.erreur, 'erreur');
      } finally {
        this.envoi = false;
        this.bouton.removeAttribute('aria-busy');
        this.classList.remove('gc-produit--envoi');
      }
    }
  }

  /* --- Barre collante ---------------------------------------------------- */
  class GcBarreProduit extends HTMLElement {
    connectedCallback() {
      if (this.pret) return;
      this.pret = true;
      this.libelles = Array.from(this.querySelectorAll('[data-gc-barre-libelle]'));
      this.prix = Array.from(this.querySelectorAll('[data-gc-barre-prix]'));
      this.boutons = Array.from(this.querySelectorAll('[data-gc-barre-bouton]'));
      this.mobile = this.querySelector('[data-gc-barre-mobile]');

      document.addEventListener('gc:variante', (evt) => {
        if (!evt.detail || String(evt.detail.produit) !== this.dataset.produit) return;
        this.maj(evt.detail.variante);
      });
      this.mesurerEntete();
      this.observerBouton();
      this.reserverEspace();
      this.observerOnglets();
    }

    /* En-tête collant (lot A) : la barre se colle juste dessous, à la hauteur réelle mesurée. */
    mesurerEntete() {
      const entete = document.querySelector('[data-gc-entete]');
      const enveloppe = entete ? entete.closest('.shopify-section') || entete : null;
      if (!enveloppe) return;
      const racine = document.documentElement;
      const poser = () => {
        const position = getComputedStyle(enveloppe).position;
        const collant = position === 'sticky' || position === 'fixed';
        racine.style.setProperty('--gc-entete-decalage', collant ? `${enveloppe.offsetHeight}px` : '0px');
      };
      poser();
      if ('ResizeObserver' in window) new ResizeObserver(poser).observe(enveloppe);
      else window.addEventListener('resize', poser, { passive: true });
    }

    maj(variante) {
      if (variante) {
        this.libelles.forEach((el) => { el.textContent = variante.libelle; });
        this.prix.forEach((el) => { el.textContent = variante.prix; });
      }
      this.boutons.forEach((bouton) => { bouton.disabled = !(variante && variante.disponible); });
    }

    observerBouton() {
      const cible = document.getElementById(this.dataset.cible);
      if (!this.mobile || !cible || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entree]) => {
        const passe = !entree.isIntersecting && entree.boundingClientRect.top < 0;
        this.mobile.dataset.visible = String(passe);
      }).observe(cible);
    }

    reserverEspace() {
      if (!this.mobile) return;
      const racine = document.documentElement;
      racine.classList.add('gc-barre-achat');
      const poser = () => racine.style.setProperty('--gc-barre-achat-h', `${this.mobile.offsetHeight}px`);
      poser();
      if ('ResizeObserver' in window) new ResizeObserver(poser).observe(this.mobile);
      else window.addEventListener('resize', poser, { passive: true });
    }

    observerOnglets() {
      const liens = Array.from(this.querySelectorAll('[data-gc-onglet]'));
      const paires = liens
        .map((lien) => ({ lien, cible: document.getElementById(decodeURIComponent(lien.hash.slice(1))) }))
        .filter((paire) => paire.cible);
      if (!paires.length || !('IntersectionObserver' in window)) return;
      const activer = (cible) => {
        paires.forEach(({ lien, cible: c }) => {
          const actif = c === cible;
          lien.classList.toggle('gc-barre__onglet--actif', actif);
          if (actif) lien.setAttribute('aria-current', 'location');
          else lien.removeAttribute('aria-current');
        });
      };
      const io = new IntersectionObserver((entrees) => {
        entrees.forEach((entree) => { if (entree.isIntersecting) activer(entree.target); });
      }, { rootMargin: '-30% 0px -65% 0px' });
      paires.forEach(({ cible }) => io.observe(cible));
    }
  }

  /* --- Comparateur sur la fiche : choisir une hauteur sans recharger ----- */
  document.addEventListener('click', (evt) => {
    const lien = evt.target.closest('[data-gc-choisir-variante]');
    if (!lien) return;
    const produit = document.querySelector(`gc-produit[data-produit="${lien.dataset.gcProduit}"]`);
    if (!produit || typeof produit.choisirVariante !== 'function') return;
    if (!produit.choisirVariante(lien.dataset.gcChoisirVariante)) return;
    evt.preventDefault();
    const options = produit.querySelector('[data-gc-options]') || produit;
    options.scrollIntoView({ behavior: mouvementReduit() ? 'auto' : 'smooth', block: 'center' });
    const coche = options.querySelector('[data-gc-choix]:checked');
    if (coche) coche.focus({ preventScroll: true });
  });

  if (!customElements.get('gc-galerie')) customElements.define('gc-galerie', GcGalerie);
  if (!customElements.get('gc-produit')) customElements.define('gc-produit', GcProduit);
  if (!customElements.get('gc-barre-produit')) customElements.define('gc-barre-produit', GcBarreProduit);
})();
